import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import classNames from 'classnames'
import Switch from 'react-switch'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import SupportModal from 'components/modals/supportModal'
import { RootState } from 'store/redux'
import { toggleSupportModal } from 'store/redux/sidebar'
import { faPlain, toman } from 'utilities/helper/format'
import {
    calculateSubscriptionPrice,
    initSubscription,
    setSubscriptionPayment,
} from 'services/plan'
import {
    getActiveGateways,
    useActiveGatewayCode,
} from 'utilities/hooks/useIsDefaultGateway'
import { apiMessage } from 'modules/cashbox/ui'
import useGatewayHandoff from 'utilities/hooks/useGatewayHandoff'
import { useCashbox } from 'modules/cashbox/hooks'
import useFinishFlow, { FLOWS } from 'utilities/navigation/flow'
import { rememberPaymentFlow } from 'utilities/analytics/paymentFlow'
import { goToGateway } from 'modules/gatewayHandoff'

/** Server-priced per 50 members, so each option asks the backend. */
function useCashboxPrice(boxId?: string, monthCount?: number) {
    const { data, isLoading } = useQuery(
        ['subscription-price', boxId, monthCount, 'csb'],
        calculateSubscriptionPrice,
        { enabled: !!boxId && !!monthCount }
    )
    return { price: data?.amount, isLoading }
}

/** Same bill row as the moneypool subscription screen. */
function BillRow({
    label,
    value,
    tone,
}: {
    label: string
    value: string
    tone?: 'total'
}) {
    return (
        <div className=" flex items-center justify-between gap-2 border-b border-divider py-2.5 last:border-b-0">
            <span className=" text-xs text-text-secondary">{label}</span>
            <span
                className={classNames(
                    'text-sm',
                    tone === 'total'
                        ? ' font-bold text-heading'
                        : ' text-text-body'
                )}
            >
                {value}
            </span>
        </div>
    )
}

/**
 * خرید اشتراک صندوق قرعه‌کشی.
 *
 * Same v2 gateway flow the moneypool uses, with `itemType: 'csb'` — which is
 * what attaches the Order to the Cashbox and lets the payment callback clear
 * the box's `unp` state (`payment/utils.py` moves it to `nct`, or `dft` for a
 * draft box).
 *
 * Styled off `pages/fund/subscription` so the two subscription screens read as
 * the same product: canvas background, brand tokens, «مدت اشتراک» / «روش
 * پرداخت» sections, gateway logos rather than raw `unique_code`s, and the pay
 * button pinned to a bottom bar instead of floating after the content.
 */
export default function CashboxSubscriptionPage() {
    const { id } = useParams()
    const { cashbox } = useCashbox(id)
    const dispatch = useDispatch()
    const { isOpenSupportModal } = useSelector(
        (state: RootState) => state.sideBarControler
    )

    const [selectedCode, setSelectedCode] = useState<string | null>(null)
    const [monthCount, setMonthCount] = useState<number | null>(null)

    const profile = JSON.parse(localStorage.getItem('profile') ?? 'null')
    const { data: initData, isLoading: initLoading } = useQuery(
        ['init-subscription', profile?.id],
        initSubscription
    )
    useActiveGatewayCode(initData, setSelectedCode)
    const gateways = getActiveGateways(initData)

    const monthly = useCashboxPrice(id, 1)
    const quarterly = useCashboxPrice(id, 3)
    const pricesLoading = monthly.isLoading || quarterly.isLoading

    // An interval with no configured price comes back as 0; a «۰ تومان» card
    // would only fail at the gateway, so it is left out.
    //
    // بستهٔ یک‌ساله در نسخهٔ وب فروخته نمی‌شود (work item #72) — قیمت
    // ۱۲‌ماهه هم دیگر از بک‌اند گرفته نمی‌شود.
    const plans = [
        { months: 1, title: 'اشتراک یک‌ماهه', price: monthly.price },
        { months: 3, title: 'اشتراک سه‌ماهه', price: quarterly.price },
    ].filter((plan) => !!plan.price && plan.price > 0)

    // Wait for BOTH prices before defaulting. Keying on `plans.length` alone
    // made the default whichever request happened to land first: if the
    // 3-month price arrived on its own it was the only entry, so `plans[0]`
    // selected it, and it then stayed selected once the 1-month price filled
    // in — the page opened on the pricier card for no reason the user could
    // see. Bailing while `pricesLoading` keeps `plans` complete at the moment
    // the choice is made.
    useEffect(() => {
        if (pricesLoading) return
        if (plans.length && !plans.some((plan) => plan.months === monthCount))
            setMonthCount(plans[0].months)
    }, [pricesLoading, plans.length, monthCount]) // eslint-disable-line react-hooks/exhaustive-deps

    const selectedPrice = plans.find(
        (plan) => plan.months === monthCount
    )?.price

    // The wallet. `/payment/init/` — the same call that fetches the gateway
    // list above — already answers with the balance, so this screen had the
    // number in hand the whole time and simply never read it. A cashbox
    // manager with money in Hamyan still had to reach for a card to renew.
    //
    // A subscription item carries no commission, so the amount to cover is just
    // the price.
    const balance = Number(initData?.wallet_balance ?? 0)
    const [useWallet, setUseWallet] = useState(false)
    const due = selectedPrice ?? 0
    const hasWallet = balance > 0 && due > 0
    const walletCoversAll = useWallet && due > 0 && balance >= due
    const fromWallet = useWallet ? Math.min(balance, due) : 0
    const remaining = useWallet ? Math.max(0, due - balance) : due
    // Covered in full ⇒ no bank, so nothing to pick. This also makes the
    // purchase reachable when every gateway is down.
    const needsGateway = !walletCoversAll

    const remainingDays = cashbox?.service_package_remaining_days
    const isRenewal = typeof remainingDays === 'number' && remainingDays > 0

    // Once, after the prices and the gateway list have both landed — before
    // that the page has nothing on it and «چند بسته دیدی» would read zero for
    // everybody.
    const viewed = useRef(false)
    useEffect(() => {
        if (viewed.current || initLoading || pricesLoading) return
        viewed.current = true
        posthog.capture('subscription_page_viewed', {
            box_id: id,
            box_type: 'csb',
            is_renewal: isRenewal,
            remaining_days: remainingDays ?? null,
            plans_available: plans.length,
            gateways_available: gateways.length,
        })
    }, [initLoading, pricesLoading]) // eslint-disable-line react-hooks/exhaustive-deps

    const handoff = useGatewayHandoff()
    const finishFlow = useFinishFlow(FLOWS.cashboxPlan)
    const pay = useMutation({
        mutationFn: () =>
            setSubscriptionPayment({
                // Deliberately absent when the wallet settles the whole
                // price: naming a bank would make the backend run its
                // per-gateway kill-switch against one it is not going to call,
                // and refuse a purchase that needs no bank at all.
                gatewayCode: needsGateway ? selectedCode : null,
                useWallet,
                monthCount: monthCount!,
                ctxId: Number(id),
                amount: selectedPrice!,
                boxType: 'csb',
            }),
        onSuccess: (result: any) => {
            // Settled out of the wallet: the backend created the Order, booked
            // the payment and activated the package itself. There is no bank to
            // visit, so there is no `payment_link` to follow and nothing for the
            // gateway-callback page to finish — leaving a
            // `toBePaidTransactions` record behind would hand it work for a
            // payment that is already done.
            if (result?.paid_from_wallet) {
                handoff.release()
                posthog.capture('payment_wallet_paid', {
                    entry: 'cashbox_subscription',
                    amount: selectedPrice,
                    month_count: monthCount,
                    box_id: id,
                })
                toast.success('اشتراک صندوق از کیف پول خریداری شد')
                finishFlow(`/cashbox/${id}`)
                return
            }
            // Stored before the capture: this is what the funds landing reads
            // back to name the flow once the bank returns, and localStorage is
            // synchronous, so it survives the unload regardless.
            rememberPaymentFlow({
                flow_type: 'cashbox_subscription',
                amount: selectedPrice ?? null,
                gateway: selectedCode,
                box_id: id ?? null,
                box_type: 'csb',
                month_count: monthCount,
            })
            // sendBeacon, because `window.location.replace` two lines down
            // unloads the page before an XHR capture gets to leave — this is
            // the last event of the session on the gateway path.
            posthog.capture(
                'payment_gateway_redirect',
                {
                    flow_type: 'cashbox_subscription',
                    cashbox_id: id,
                    month_count: monthCount,
                    gateway: selectedCode,
                    use_wallet: useWallet,
                },
                { transport: 'sendBeacon' }
            )
            localStorage.setItem(
                'toBePaidTransactions',
                JSON.stringify({ type: 'subscription', data: [] })
            )
            // The button stays down while the browser is on its way to the
            // bank — see `useGatewayHandoff`.
            handoff.hold()
            goToGateway(result?.payment_link)
        },
        onError: (error: any) => {
            handoff.release()
            posthog.capture('subscription_purchase_failed', {
                flow_type: 'cashbox_subscription',
                cashbox_id: id,
                month_count: monthCount,
                status: error?.response?.status,
            })
            toast.error(apiMessage(error))
        },
    })

    // The dead end this screen can land on: with no priced plan it shows
    // «امکان خرید اشتراک وجود ندارد» and the box stays in `unp` forever. Both
    // counts are needed — no plans and no gateways are different failures with
    // the same blank screen.
    const plansAvailable = plans.length
    const gatewaysAvailable = gateways.length
    useEffect(() => {
        if (initLoading || pricesLoading) return
        posthog.capture('cashbox_subscription_viewed', {
            cashbox_id: id,
            plans_available: plansAvailable,
            gateways_available: gatewaysAvailable,
        })
    }, [initLoading, pricesLoading, id, plansAvailable, gatewaysAvailable])

    if (initLoading) return <DotsLoading />

    return (
        <>
            <div className=" flex min-h-screen flex-col bg-canvas">
                <div className=" grow-0">
                    <Header
                        title={
                            isRenewal
                                ? 'تمدید اشتراک صندوق'
                                : 'خرید اشتراک صندوق'
                        }
                    />
                </div>

                <div className=" flex grow flex-col px-4 pb-6">
                    {isRenewal && (
                        <p className=" mt-4 rounded-card bg-brand-soft p-3 text-sm leading-6 text-brand-deep">
                            اشتراک فعلی صندوق {faPlain.format(remainingDays!)}{' '}
                            روز دیگر اعتبار دارد. این روزها از دست نمی‌رود و به
                            دوره‌ای که خریداری می‌کنید اضافه می‌شود.
                        </p>
                    )}

                    <p className=" mt-4 text-sm leading-6 text-text-secondary">
                        {cashbox?.name ? (
                            <>
                                صندوق{' '}
                                <span className=" font-bold text-heading">
                                    {cashbox.name}
                                </span>
                                {' — '}
                            </>
                        ) : null}
                        قیمت اشتراک بر اساس تعداد اعضای صندوق محاسبه می‌شود.
                    </p>

                    {pricesLoading && (
                        <div className=" py-6">
                            <DotsLoading />
                        </div>
                    )}

                    {!pricesLoading && !plans.length && (
                        <p className=" mt-6 rounded-card bg-danger-soft p-3 text-center text-sm leading-6 text-danger-on">
                            در حال حاضر امکان خرید اشتراک برای این صندوق وجود
                            ندارد. لطفاً با مشاور همیان تماس بگیرید.
                        </p>
                    )}

                    {!!plans.length && (
                        <>
                            <h2 className=" mt-5 text-sm font-bold text-heading">
                                مدت اشتراک
                            </h2>
                            <div className=" mt-2 grid grid-cols-2 gap-2">
                                {plans.map((plan) => {
                                    const isSelected = monthCount === plan.months
                                    return (
                                        <button
                                            key={plan.months}
                                            type="button"
                                            aria-pressed={isSelected}
                                            onClick={() =>
                                                setMonthCount(plan.months)
                                            }
                                            className={classNames(
                                                'rounded-card border px-3 py-3 text-right transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                                                isSelected
                                                    ? ' border-brand bg-brand-soft'
                                                    : ' border-stroke bg-white'
                                            )}
                                        >
                                            <span
                                                className={classNames(
                                                    'block text-sm font-bold',
                                                    isSelected
                                                        ? ' text-brand-deep'
                                                        : ' text-text-body'
                                                )}
                                            >
                                                {plan.title}
                                            </span>
                                            <span
                                                className={classNames(
                                                    'mt-1 block text-xs',
                                                    isSelected
                                                        ? ' text-brand-deep'
                                                        : ' text-text-secondary'
                                                )}
                                            >
                                                {toman(plan.price!)}
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>

                            {hasWallet && (
                                <div className=" mt-5 flex items-center justify-between gap-3 rounded-card bg-white px-3 py-2.5 shadow-card">
                                    <div className=" min-w-0">
                                        <p className=" text-sm text-text-body">
                                            استفاده از کیف پول
                                        </p>
                                        <p className=" text-xs text-text-secondary">
                                            موجودی {toman(balance)}
                                        </p>
                                    </div>
                                    <Switch
                                        onChange={() => setUseWallet(!useWallet)}
                                        checked={useWallet}
                                        onColor="#75B844"
                                        onHandleColor="#EAEEF0"
                                        handleDiameter={22}
                                        uncheckedIcon={false}
                                        checkedIcon={false}
                                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                        activeBoxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                        height={26}
                                        width={50}
                                        className="react-switch shrink-0"
                                        offColor="#B6BCC8"
                                    />
                                </div>
                            )}

                            {/* Gateway — pointless when there is nothing left
                                for a bank to take */}
                            {needsGateway && (
                                <>
                                <h2 className=" mt-5 text-sm font-bold text-heading">
                                    روش پرداخت
                                </h2>
                                {gateways.length === 0 ? (
                                    <p className=" mt-2 rounded-card bg-danger-soft p-3 text-sm leading-6 text-danger-on">
                                        درگاه پرداخت فعالی در دسترس نیست.
                                    </p>
                                ) : (
                                    <div className=" mt-2 flex flex-wrap gap-3">
                                        {gateways.map((gateway) => (
                                            <button
                                                key={gateway.unique_code}
                                                type="button"
                                                aria-pressed={
                                                    selectedCode ===
                                                    gateway.unique_code
                                                }
                                                onClick={() =>
                                                    setSelectedCode(
                                                        gateway.unique_code
                                                    )
                                                }
                                                className={classNames(
                                                    'rounded-card border bg-white p-1 transition-colors',
                                                    selectedCode ===
                                                        gateway.unique_code
                                                        ? ' border-brand'
                                                        : ' border-stroke'
                                                )}
                                            >
                                                {gateway.img_url ? (
                                                    <img
                                                        src={gateway.img_url}
                                                        alt={gateway.unique_code}
                                                        className=" h-12"
                                                    />
                                                ) : (
                                                    <span className=" block px-3 py-4 text-xs text-text-body">
                                                        {gateway.unique_code}
                                                    </span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                </>
                            )}

                            <div className=" mt-5 rounded-card bg-white px-3 py-1 shadow-card">
                                <BillRow
                                    label="ارزش بسته اشتراک"
                                    value={
                                        selectedPrice ? toman(selectedPrice) : '—'
                                    }
                                />
                                {useWallet && (
                                    <BillRow
                                        label="از کیف پول"
                                        value={toman(fromWallet)}
                                    />
                                )}
                                <BillRow
                                    label="مبلغ قابل پرداخت"
                                    value={
                                        selectedPrice ? toman(remaining) : '—'
                                    }
                                    tone="total"
                                />
                            </div>
                        </>
                    )}

                    <div className=" mt-auto flex flex-col items-center pb-4 pt-8 text-center">
                        <p className=" text-xs text-text-secondary">
                            نیاز به راهنمایی دارید؟
                        </p>
                        <button
                            type="button"
                            className=" mt-1 text-sm font-bold text-brand-deep underline underline-offset-4"
                            onClick={() =>
                                dispatch(
                                    toggleSupportModal({
                                        isOpenSupportModal: true,
                                    })
                                )
                            }
                        >
                            با مشاور همیان تماس بگیرید
                        </button>
                    </div>
                </div>

                <div className=" grow-0 border-t border-divider bg-white px-4 py-3">
                    <button
                        type="button"
                        className=" w-full rounded-card bg-brand py-3 text-sm font-bold text-white transition-colors active:bg-brand-strong disabled:opacity-50"
                        disabled={
                            (needsGateway && !selectedCode) ||
                            !monthCount ||
                            !selectedPrice ||
                            pay.isLoading ||
                            handoff.locked
                        }
                        onClick={() => {
                            // Refused by the ref, not by `disabled`: a second
                            // tap in the same tick lands before React has
                            // re-rendered the button.
                            if (handoff.isLocked()) return
                            handoff.lock()
                            posthog.capture('subscription_purchase_initiated', {
                                flow_type: 'cashbox_subscription',
                                amount: selectedPrice,
                                month_count: monthCount,
                                cashbox_id: id,
                                gateway: walletCoversAll ? 'wallet' : selectedCode,
                                use_wallet: useWallet,
                                wallet_covers_all: walletCoversAll,
                            })
                            pay.mutate()
                        }}
                    >
                        {pay.isLoading || handoff.locked
                            ? walletCoversAll
                                ? 'در حال پرداخت…'
                                : 'در حال انتقال به درگاه…'
                            : walletCoversAll
                            ? `پرداخت ${toman(due)} از کیف پول`
                            : selectedPrice
                            ? `پرداخت ${toman(remaining)}`
                            : 'پرداخت'}
                    </button>
                </div>
            </div>

            <SupportModal
                show={isOpenSupportModal}
                onHide={() =>
                    dispatch(toggleSupportModal({ isOpenSupportModal: false }))
                }
            />
        </>
    )
}
